import { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import { shuffle } from '@/utils/shuffle';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';

//Get n random items out of arr
function getRandom(arr: any[], count: number) {
  if (arr.length < count) {
    count = arr.length;
  }
  let shuffled = arr.slice(0),
    i = arr.length,
    min = i - count,
    temp,
    index;
  while (i-- > min) {
    index = Math.floor((i + 1) * Math.random());
    temp = shuffled[index];
    shuffled[index] = shuffled[i];
    shuffled[i] = temp;
  }
  return shuffled.slice(min);
}

export function useBildkartenExercise(
  setId: string,
  exerciseClass: string,
  foundSet: any,
) {
  const { getLocalUri } = useAssetDownloader();
  const initialZielwortValue =
    exerciseClass == 'wordset' ? 'Zielwort' : 'Zielsatz';
  const randomNumbersRef = useRef<number[]>([]);
  const randomRef = useRef<number>();
  const timeoutRef = useRef<any>();

  const [_images, setImages] = useState<string[]>([]);
  const [zielwort, setZielwort] = useState(initialZielwortValue);
  const activeSet = foundSet?.activeWordset?.[exerciseClass];
  const activeSetSettings = foundSet?.cardSettings;

  const isAuset = activeSetSettings.setting == 'wordset';
  const variableKey = exerciseClass == 'wordset' ? 'word' : 'sentence';

  const getRandomNumbers = useCallback(() => {
    const randomNumbers: number[] = [];
    activeSet?.[exerciseClass + 'Items']?.forEach(
      (item: any, index: number) => {
        randomNumbers.push(index);
      },
    );
    shuffle(randomNumbers);
    if (
      activeSetSettings.setting === 'wordset' &&
      activeSetSettings.images < activeSet[exerciseClass + 'Items'].length
    ) {
      let modulo =
        activeSet[exerciseClass + 'Items'].length % activeSetSettings.images;
      if (modulo > 0) {
        for (let i = 0; i < activeSetSettings.images - modulo; i++) {
          randomNumbers.push(randomNumbers[i]);
        }
      }
    }
    randomNumbersRef.current = randomNumbers;
  }, [setId, activeSet, activeSetSettings]);

  const getCard = useCallback(async () => {
    let random: number;
    let ablenker;
    let correctImage;

    if (activeSetSettings.setting !== 'wordset') {
      if (randomNumbersRef.current.length < 1) {
        getRandomNumbers();
      }
      randomRef.current = randomNumbersRef.current.pop();

      const setItem =
        activeSet[exerciseClass + 'Items'][randomRef.current ?? 0];
      //use retina image for one card setting and check if retina exists for backwards compatibility
      if (setItem[variableKey].retinaImage && activeSetSettings.images === 1) {
        correctImage = setItem[variableKey].retinaImage;
      } else {
        correctImage = setItem[variableKey].pictures[0];
      }
      if (activeSetSettings.setting == 'related') {
        ablenker = setItem.semanticPictures;
      } else if (activeSetSettings.setting == 'unrelated') {
        ablenker = setItem.unrelatedPictures;
      } else if (activeSetSettings.setting == 'wordset') {
        ablenker = [];
        activeSet[exerciseClass + 'Items'].forEach(() => {
          if (
            activeSet[exerciseClass + 'Items'][randomRef.current ?? 0][
              variableKey
            ]._id !== setItem[variableKey]._id
          ) {
            ablenker.push(setItem[variableKey].exerciseClass.pictures[0]);
          }
        });
      }
      let pictures = getRandom(ablenker, activeSetSettings.images - 1);
      pictures.push(correctImage);
      const images: string[] = [];
      for (let picture of pictures) {
        const uri = await getLocalUri(picture);
        images.push(uri);
      }
      shuffle(images);
      setImages(images);
    } else {
      let img;
      const images = [];
      if (
        activeSetSettings.images >= activeSet[exerciseClass + 'Items'].length
      ) {
        for (let wordsetItem of activeSet[exerciseClass + 'Items']) {
          if (
            wordsetItem[variableKey].retinaImage &&
            activeSetSettings.images === 1
          ) {
            img = wordsetItem[variableKey].retinaImage;
          } else {
            img = wordsetItem[variableKey].pictures[0];
          }
          const uri = await getLocalUri(img);
          images.push(uri);

          shuffle(images);
        }
        setImages(images);
      } else {
        let wordsetItem;
        for (let i = 0; i < activeSetSettings.images; i++) {
          if (randomNumbersRef.current.length < 1) {
            getRandomNumbers();
          }
          random = randomNumbersRef.current.pop() ?? 0;
          wordsetItem = activeSet[exerciseClass + 'Items'][random];
          if (
            wordsetItem[variableKey].retinaImage &&
            activeSetSettings.images === 1
          ) {
            img = wordsetItem[variableKey].retinaImage;
          } else {
            img = wordsetItem[variableKey].pictures[0];
          }
          const uri = await getLocalUri(img);
          images.push(uri);

          shuffle(images);
        }
        setImages(images);
      }
    }
  }, [activeSet, activeSetSettings]);

  const generate = useCallback(() => {
    getRandomNumbers();
    getCard();
  }, [getRandomNumbers, getCard]);

  const showCorrectWord = useCallback(() => {
    setZielwort(
      activeSet[exerciseClass + 'Items'][randomRef.current ?? 0]?.[variableKey]
        ?.print,
    );
    timeoutRef.current = setTimeout(() => {
      setZielwort(initialZielwortValue);
    }, 1000);
  }, [activeSet]);

  useEffect(() => {
    generate();
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return { _images, generate, zielwort, showCorrectWord, isAuset };
}
